import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Privacy policy for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-931">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-932">
        <div className="max-w-4xl mx-auto text-center" data-id="element-933">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-934">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-935">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-936">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-937">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-938">
            <p data-id="element-939">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-940">
              <li data-id="element-941">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-942">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-943">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-944">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-945">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-946">
              <ul className="list-disc pl-6 space-y-2" data-id="element-947">
                <li data-id="element-948">
                  <strong data-id="element-949">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-950">
                  <strong data-id="element-951">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-952">
                  <strong data-id="element-953">Do we receive information from third parties?</strong>{' '}
                  We do not receive information from third parties.
                </li>
                <li data-id="element-954">
                  <strong data-id="element-955">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-956">
                  <strong data-id="element-957">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-958">
                  <strong data-id="element-959">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-960">
                  <strong data-id="element-961">What are your rights?</strong> Depending on your
                  location, you may have rights under applicable privacy laws.
                </li>
                <li data-id="element-962">
                  <strong data-id="element-963">How do you exercise your rights?</strong> You can
                  exercise rights by contacting us (see Section 11).
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-964">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-965">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-966">
              <li data-id="element-967">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-968">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-969">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-970">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-971">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-972">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-973">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-974">WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li data-id="element-975">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-976">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-977">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-978">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-979">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-980">
              <h3 className="font-bold text-lg" data-id="element-981">
                Personal information you disclose to us
              </h3>
              <p data-id="element-982">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-983">
                <strong data-id="element-984">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-985">
                <li data-id="element-986">Names</li>
                <li data-id="element-987">Phone numbers</li>
                <li data-id="element-988">Email addresses</li>
                <li data-id="element-989">Mailing addresses</li>
                <li data-id="element-990">Education level and interests</li>
                <li data-id="element-991">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-992">
                Information automatically collected
              </h3>
              <p data-id="element-993">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-994">
                <li data-id="element-995">IP address</li>
                <li data-id="element-996">Browser and device characteristics</li>
                <li data-id="element-997">Operating system</li>
                <li data-id="element-998">Language preferences</li>
                <li data-id="element-999">Referring URLs</li>
                <li data-id="element-1000">Device name and type</li>
                <li data-id="element-1001">Pages viewed and time spent</li>
                <li data-id="element-1002">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-1003">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1004">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1005">
              <p data-id="element-1006">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1007">
                <li data-id="element-1008">To facilitate account creation and authentication</li>
                <li data-id="element-1009">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-1010">To respond to user inquiries and offer support</li>
                <li data-id="element-1011">To send administrative information to you</li>
                <li data-id="element-1012">To fulfill and manage your requests</li>
                <li data-id="element-1013">To enable user-to-user communications</li>
                <li data-id="element-1014">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-1015">To send you marketing and promotional communications</li>
                <li data-id="element-1016">To deliver targeted advertising to you</li>
                <li data-id="element-1017">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-1018">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-1019">To save or protect an individual's vital interest</li>
                <li data-id="element-1020">To comply with our legal obligations</li>
              </ul>
            </div>
          </div>

          {/* 3. When and With Whom Do We Share */}
          <div data-id="element-1021">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1022">
              3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1023">
              <p data-id="element-1024">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1025">
                <li data-id="element-1026">
                  <strong data-id="element-1027">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-1028">
                  <strong data-id="element-1029">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-1030">
                  <strong data-id="element-1031">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-1032">
                  <strong data-id="element-1033">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-1034">
                  <strong data-id="element-1035">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 4. Cookies and Tracking */}
          <div data-id="element-1036">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1037">
              4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1038">
              <p data-id="element-1039">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 5. How Long Do We Keep */}
          <div data-id="element-1040">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1041">
              5. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1042">
              <p data-id="element-1043">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 6. How Do We Keep Information Safe */}
          <div data-id="element-1044">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1045">
              6. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1046">
              <p data-id="element-1047">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 7. Minors */}
          <div data-id="element-1048">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1049">
              7. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1050">
              <p data-id="element-1051">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 8. Privacy Rights */}
          <div data-id="element-1052">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1053">
              8. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1054">
              <p data-id="element-1055">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1056">
                <li data-id="element-1057">The right to access your personal information</li>
                <li data-id="element-1058">
                  The right to request correction of your personal information
                </li>
                <li data-id="element-1059">
                  The right to request deletion of your personal information
                </li>
                <li data-id="element-1060">The right to opt out of marketing communications</li>
                <li data-id="element-1061">
                  The right to opt out of the sale or sharing of personal
                  information (where applicable)
                </li>
              </ul>
              <p className="mt-4" data-id="element-1062">
                To exercise these rights, please contact us using the
                information in Section 11.
              </p>
            </div>
          </div>

          {/* 9. Do Not Track */}
          <div data-id="element-1063">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1064">
              9. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1065">
              <p data-id="element-1066">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized.
              </p>
            </div>
          </div>

          {/* 10. Updates */}
          <div data-id="element-1067">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1068">
              10. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1069">
              <p data-id="element-1070">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 11. Contact Us */}
          <div data-id="element-1071">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1072">
              11. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1073">
              <p data-id="element-1074">
                If you have questions or comments about this notice, please
                contact us through the information provided on our website.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}